import { useEffect, useState } from "react";
import { jwtFetch } from "./utils/apiClient";
import { useNavigate, useParams } from "react-router-dom";
import { format, differenceInCalendarMonths } from "date-fns";
import { config, priceFormat, shortenUrl } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const ContractEdit = () => {
  const { id_contract } = useParams();
  const navigate = useNavigate();

  const [contract, setContract] = useState(null);
  const [quotationMaster, setQuotationMaster] = useState(null);
  const [contractName, setContractName] = useState("");
  const [contractSchedules, setContractSchedules] = useState([]);
  const [contractItems, setContractItems] = useState([]);
  const [contractAttachments, setContractAttachments] = useState([]);
  const [contractRemarks, setContractRemarks] = useState("");
  const [contractStatus, setContractStatus] = useState("Active");
  const [initialScheduleIds, setInitialScheduleIds] = useState([]);
  const [initialItemIds, setInitialItemIds] = useState([]);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);

  useEffect(() => {
    if (id_contract) {
      getContract();
      getContractItems();
      getContractSchedules();
    }
  }, [id_contract]);

  const getContract = async () => {
    const fetchContract = await jwtFetch(config.ENDPOINT + "get/contract", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        id_contract: id_contract,
      }),
    });
    const responseGetContract = await fetchContract.json();
    if (responseGetContract.status === 200 && responseGetContract.data.length > 0) {
      const contractData = responseGetContract.data[0];
      setContract(contractData);
      setContractName(contractData.contract_name || "");
      setContractRemarks(contractData.contract_remarks || "");
      setContractStatus(contractData.contract_status || "Active");
      if (contractData.contract_attachments) {
        try {
          setContractAttachments(JSON.parse(contractData.contract_attachments));
        } catch (error) {
          setContractAttachments([]);
        }
      } else {
        setContractAttachments([]);
      }
      if (contractData.quotation_id) {
        getQuotationMaster(contractData.quotation_id);
      } else {
        setQuotationMaster(null);
      }
    } else {
      alert(responseGetContract.message || "Contract data not found");
    }
  };

  const getQuotationMaster = async (quotationId) => {
    try {
      const fetchQuotation = await jwtFetch(config.ENDPOINT + "get/quotation", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id_quotation: quotationId,
        }),
      });
      const responseGetQuotation = await fetchQuotation.json();
      if (responseGetQuotation.status === 200 && responseGetQuotation.data.length > 0) {
        setQuotationMaster(responseGetQuotation.data[0]);
      } else {
        setQuotationMaster({
          id_quotation: quotationId,
          quotation_code: null,
        });
      }
    } catch (error) {
      console.error('Error in getQuotationMaster:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getContractItems = async () => {
    try {
      const fetchContractItems = await jwtFetch(config.ENDPOINT + "get/contract_item", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          contract_id: id_contract,
        }),
      });
      const responseGetContractItems = await fetchContractItems.json();
      if (responseGetContractItems.status === 200) {
        setContractItems(responseGetContractItems.data || []);
        setInitialItemIds((responseGetContractItems.data || []).map((item) => item.id_contract_item));
      } else {
        alert(responseGetContractItems.message);
      }
    } catch (error) {
      console.error('Error in getContractItems:', error);
      alert('An error occurred: ' + (error.message || error));
    }
  };

  const getContractSchedules = async () => {
    const fetchContractSchedules = await jwtFetch(config.ENDPOINT + "get/contract_schedule", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        contract_id: id_contract,
      }),
    });
    const responseGetContractSchedules = await fetchContractSchedules.json();
    if (responseGetContractSchedules.status === 200) {
      const toDateInputValue = (value) => {
        if (!value) {
          return "";
        }
        if (typeof value === "string" && value.length >= 10) {
          return value.slice(0, 10);
        }
        try {
          return format(new Date(value), "yyyy-MM-dd");
        } catch (error) {
          return "";
        }
      };
      const schedules = (responseGetContractSchedules.data || []).map((schedule) => ({
        ...schedule,
        schedule_start_date: toDateInputValue(schedule.schedule_start_date),
        schedule_end_date: toDateInputValue(schedule.schedule_end_date),
        error_date: false,
      }));
      setContractSchedules(schedules);
      setInitialScheduleIds(schedules.map((schedule) => schedule.id_contract_schedule));
    } else {
      alert(responseGetContractSchedules.message);
    }
  };

  const removeItem = (index) => {
    contractItems.splice(index, 1);
    setContractItems([...contractItems]);
  };

  const addSchedule = () => {
    const schedule = {
      schedule_start_date: format(new Date(), "yyyy-MM-dd"),
      schedule_end_date: null,
      schedule_long_period: 1,
      schedule_status: "Active",
      error_date: true,
    };
    setContractSchedules([...contractSchedules, schedule]);
  };

  const changeScheduleData = (column, value, index) => {
    const schedule = contractSchedules[index];
    schedule[column] = value;
    if (schedule.schedule_start_date && schedule.schedule_end_date) {
      try {
        const start = new Date(schedule.schedule_start_date + "T00:00:00");
        const end = new Date(schedule.schedule_end_date + "T00:00:00");
        const base = differenceInCalendarMonths(end, start);
        const months = base + 1;
        schedule.schedule_long_period = base < 0 ? 0 : months;
        schedule.error_date = end < start;
      } catch (e) {
        schedule.schedule_long_period = 0;
        schedule.error_date = true;
      }
    }
    setContractSchedules([...contractSchedules]);
  };

  const removeSchedule = (index) => {
    contractSchedules.splice(index, 1);
    setContractSchedules([...contractSchedules]);
  };

  const addAttachments = () => {
    const attachment = {
      name: "",
      files: "",
      loading: false,
    };
    setContractAttachments([...contractAttachments, attachment]);
  };

  const changeAttachmentData = (column, value, index) => {
    const attachment = contractAttachments[index];
    attachment[column] = value;
    if (column === "files") {
      attachment.loading = false;
    }
    setContractAttachments([...contractAttachments]);
  };

  const uploadAttachments = async (files, index) => {
    changeAttachmentData("loading", true, index);
    const formData = new FormData();
    files.forEach((attachment) => {
      formData.append("files", attachment);
    });
    try {
      const responseUploadAttachments = await jwtFetch(config.ENDPOINT + "upload/gdrive", {
        method: "POST",
        body: formData,
      });
      if (!responseUploadAttachments.ok) {
        throw new Error(`Upload failed with status: ${responseUploadAttachments.status}`);
      }
      const resultUploadAttachments = await responseUploadAttachments.json();
      if (resultUploadAttachments.status === 200) {
        changeAttachmentData("files", resultUploadAttachments.data, index);
      } else {
        alert("Upload error: " + resultUploadAttachments.message);
      }
    } catch (error) {
      alert("Upload error: " + error.message);
    } finally {
      changeAttachmentData("loading", false, index);
    }
  };

  const removeAttachments = (index) => {
    contractAttachments.splice(index, 1);
    setContractAttachments([...contractAttachments]);
  };

  const submitContractEdit = async (e) => {
    e.preventDefault();
    setIsLoadingSubmit(true);

    if (contractSchedules.some((schedule) => schedule.error_date)) {
      alert("Please fix invalid schedule dates first.");
      setIsLoadingSubmit(false);
      return;
    }

    const requestContractEdit = {
      id_contract: id_contract,
      contract_name: contractName,
      contract_remarks: contractRemarks,
      contract_status: contractStatus,
      contract_attachments: contractAttachments.length > 0 ? JSON.stringify(contractAttachments) : null,
      contract_url: contract?.contract_url || null,
    };

    try {
      const fetchContractEdit = await jwtFetch(config.ENDPOINT + "update/contract", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestContractEdit),
      });
      const responseContractEdit = await fetchContractEdit.json();
      if (responseContractEdit.status !== 200) {
        alert(responseContractEdit.message);
        setIsLoadingSubmit(false);
        return;
      }

      const currentScheduleIds = contractSchedules
        .filter((schedule) => schedule.id_contract_schedule)
        .map((schedule) => schedule.id_contract_schedule);
      const deletedScheduleIds = initialScheduleIds.filter((id) => !currentScheduleIds.includes(id));
      for (const idSchedule of deletedScheduleIds) {
        const fetchDeleteSchedule = await jwtFetch(config.ENDPOINT + "delete/contract_schedule", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id_contract_schedule: idSchedule,
          }),
        });
        const responseDeleteSchedule = await fetchDeleteSchedule.json();
        if (responseDeleteSchedule.status !== 200) {
          alert(responseDeleteSchedule.message || "Failed to delete schedule.");
          setIsLoadingSubmit(false);
          return;
        }
      }

      for (const schedule of contractSchedules) {
        const schedulePayload = {
          contract_id: id_contract,
          schedule_start_date: schedule.schedule_start_date,
          schedule_end_date: schedule.schedule_end_date,
          schedule_long_period: schedule.schedule_long_period,
          schedule_status: schedule.schedule_status || "Active",
        };
        if (schedule.id_contract_schedule) {
          const fetchUpdateSchedule = await jwtFetch(config.ENDPOINT + "update/contract_schedule", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...schedulePayload,
              id_contract_schedule: schedule.id_contract_schedule,
            }),
          });
          const responseUpdateSchedule = await fetchUpdateSchedule.json();
          if (responseUpdateSchedule.status !== 200) {
            alert(responseUpdateSchedule.message || "Failed to update schedule.");
            setIsLoadingSubmit(false);
            return;
          }
        } else {
          const fetchCreateSchedule = await jwtFetch(config.ENDPOINT + "create/contract_schedule", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(schedulePayload),
          });
          const responseCreateSchedule = await fetchCreateSchedule.json();
          if (responseCreateSchedule.status !== 200) {
            alert(responseCreateSchedule.message || "Failed to create schedule.");
            setIsLoadingSubmit(false);
            return;
          }
        }
      }

      const currentItemIds = contractItems
        .filter((item) => item.id_contract_item)
        .map((item) => item.id_contract_item);
      const deletedItemIds = initialItemIds.filter((id) => !currentItemIds.includes(id));
      for (const idItem of deletedItemIds) {
        const fetchDeleteItem = await jwtFetch(config.ENDPOINT + "delete/contract_item", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id_contract_item: idItem,
          }),
        });
        const responseDeleteItem = await fetchDeleteItem.json();
        if (responseDeleteItem.status !== 200) {
          alert(responseDeleteItem.message || "Failed to delete item.");
          setIsLoadingSubmit(false);
          return;
        }
      }

      window.location.href = "/client/contract/detail/" + id_contract;
    } catch (error) {
      alert("Failed to update contract.");
      setIsLoadingSubmit(false);
    }
  };

  return (
    <div id="contract-edit">
      <div className="container-fluid p-0">
        <div className="row g-0 mh-100vh">
          <div className="col-md-2 border-end">
            <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
              <a className="h-100" href="/dashboard">
                <img className="h-100" src="/cuancuan.png" alt="PointStar" />
              </a>
            </div>
            <hr className="m-0" />
            <Menu active="clients" />
          </div>
          <div className="col-md-10">
            <Header />
            <hr className="m-0" />
            <div className="main">
              <div className="p-4">
                {contract ? (
                  <section className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div className="d-flex align-items-center">
                        <div className="fs-24">
                          <button className="btn btn-link" onClick={() => navigate(-1)}>
                            <i className="bi bi-arrow-left" />
                          </button>{" "}
                          Contract Edit
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Quotation Master</div>
                          <div className="col-md-8">
                            {contract.quotation_id ? (
                              <a href={`/quotation/detail/${contract.quotation_id}`}>
                                {quotationMaster?.quotation_code || `Quotation #${contract.quotation_id}`}{" "}
                                <i className="bi bi-link-45deg ms-1" />
                              </a>
                            ) : (
                              "-"
                            )}
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Company Group</div>
                          <div className="col-md-8">
                            {contract.company_name}{" "}
                            <a href={`/client/detail/${contract.company_id}`}>
                              <i className="bi bi-link-45deg" />
                            </a>
                          </div>
                        </div>
                        <div className="row mb-2">
                          <div className="col-md-4 text-secondary">Subsidiary</div>
                          <div className="col-md-8">
                            {contract.subcompany_name}{" "}
                            <a href={`/client/subsidiary/detail/${contract.subcompany_id}`}>
                              <i className="bi bi-link-45deg" />
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                ) : (
                  <p>No data</p>
                )}

                {contract && (
                  <section className="mb-4">
                    <form onSubmit={(e) => submitContractEdit(e)}>
                      <div className="mt-4 mb-2">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <div className="fs-24">Details</div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Contract Name <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-5">
                            <input className="form-control" type="text" value={contractName} onChange={(e) => setContractName(e.target.value)} required />
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-4">
                          <div className="col-md-2">
                            <label>Items <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-10">
                            <div className="table-responsive">
                              <table className="table table-bordered mb-2">
                                <thead>
                                  <tr>
                                    <th className="bg-secondary text-light" style={{ textAlign: "center" }}>#</th>
                                    <th className="bg-secondary text-light">SKUs</th>
                                    <th className="bg-secondary text-light" style={{ textAlign: "center" }}>Type</th>
                                    <th className="bg-secondary text-light" style={{ textAlign: "right" }}>Cost Price</th>
                                    <th className="bg-secondary text-light" style={{ textAlign: "right" }}>Selling Price</th>
                                    <th className="bg-secondary text-light"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {contractItems.map((item, s) => (
                                    <tr key={"item-" + s}>
                                      <td align="center" valign="middle">{s + 1}</td>
                                      <td align="left" valign="middle">{item.product_name}</td>
                                      <td align="left" valign="middle">{item.product_type}</td>
                                      <td align="right" valign="middle">
                                        {priceFormat(item.product_cost_original, item.product_currency_original)}
                                      </td>
                                      <td align="right" valign="middle">
                                        {priceFormat(item.product_price_original, item.product_currency_original)}
                                      </td>
                                      <td align="center">
                                        <button className="btn btn-sm btn-link text-danger" type="button" onClick={() => removeItem(s)} disabled={contractItems.length === 1}><i className="bi bi-trash" /></button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-4">
                          <div className="col-md-2">
                            <label>Payment Schedules <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-10">
                            <div className="table-responsive">
                              <table className="table table-bordered mb-2">
                                <thead>
                                  <tr>
                                    <th className="bg-secondary text-light">Schedules</th>
                                    <th className="bg-secondary text-light" style={{ textAlign: "center" }}>Period Start Date</th>
                                    <th className="bg-secondary text-light" style={{ textAlign: "center" }}>Period End Date</th>
                                    <th className="bg-secondary text-light w-80" style={{ textAlign: "center" }}>Months</th>
                                    <th className="bg-secondary text-light"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {contractSchedules.map((schedule, s) => (
                                    <tr key={"schedule-" + s}>
                                      <td valign="middle">Schedule {s + 1}</td>
                                      <td>
                                        <input
                                          className={"form-control " + (schedule.error_date ? "border-danger" : "")}
                                          type="date"
                                          value={schedule.schedule_start_date || ""}
                                          onChange={(e) => changeScheduleData("schedule_start_date", e.target.value, s)}
                                          required
                                        />
                                      </td>
                                      <td>
                                        <input
                                          className={"form-control " + (schedule.error_date ? "border-danger" : "")}
                                          type="date"
                                          value={schedule.schedule_end_date || ""}
                                          onChange={(e) => changeScheduleData("schedule_end_date", e.target.value, s)}
                                          required
                                        />
                                      </td>
                                      <td align="center" valign="middle">{schedule.schedule_long_period}</td>
                                      <td align="center">
                                        <button className="btn btn-sm btn-link text-danger" type="button" onClick={() => removeSchedule(s)} disabled={contractSchedules.length === 1}><i className="bi bi-trash" /></button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="d-flex justify-content-start">
                              <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => addSchedule()}><i className="bi bi-plus-lg me-2" /> Schedule</button>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-4">
                          <div className="col-md-2">
                            <label>Attachments</label>
                          </div>
                          <div className="col-md-10">
                            <div className="table-responsive">
                              <table className="table table-bordered mb-2">
                                <thead>
                                  <tr>
                                    <th className="bg-secondary text-light" style={{ textAlign: "center" }}>#</th>
                                    <th className="bg-secondary text-light w-400">Name</th>
                                    <th className="bg-secondary text-light">File</th>
                                    <th className="bg-secondary text-light"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {contractAttachments.map((attachment, a) => (
                                    <tr key={"attachment-" + a}>
                                      <td valign="middle" align="center">{a + 1}</td>
                                      <td valign="middle">
                                        <input className="form-control" type="text" value={attachment.name || ""} onChange={(e) => changeAttachmentData("name", e.target.value, a)} required />
                                      </td>
                                      <td valign="middle">
                                        {attachment.loading === false ? (
                                          <div>
                                            {attachment.files ? (
                                              <a className="ellipsis" href={attachment.files[0].fileUrl} target="_blank" rel="noopener noreferrer">
                                                {shortenUrl(attachment.files[0].fileUrl, 50)} <i className="bi bi-link-45deg ms-2" />
                                              </a>
                                            ) : (
                                              <input className="form-control" type="file" accept=".pdf, .png, .jpg, .jpeg, .gif, .webp, .svg, .bmp, .avif, .csv, .xls, .xlsx" onChange={(e) => uploadAttachments(Array.from(e.target.files), a)} required />
                                            )}
                                          </div>
                                        ) : (
                                          "Loading..."
                                        )}
                                      </td>
                                      <td valign="middle" align="center">
                                        <button className="btn btn-sm btn-link text-danger" type="button" onClick={() => removeAttachments(a)}><i className="bi bi-trash" /></button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="d-flex justify-content-start">
                              <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => addAttachments()}><i className="bi bi-plus-lg me-2" /> Attachment</button>
                            </div>
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Remarks</label>
                          </div>
                          <div className="col-md-10">
                            <textarea className="form-control" rows="4" value={contractRemarks} placeholder="Additional information for this Contract..." onChange={(e) => setContractRemarks(e.target.value)} />
                          </div>
                        </div>
                        <div className="row align-items-baseline form-group mb-2">
                          <div className="col-md-2">
                            <label>Status <span className="text-danger">*</span></label>
                          </div>
                          <div className="col-md-10">
                            <div className="form-check form-switch">
                              <input
                                id="contract-active"
                                className="form-check-input"
                                type="checkbox"
                                value="Active"
                                onChange={() => setContractStatus(contractStatus === "Active" ? "Inactive" : "Active")}
                                checked={contractStatus === "Active"}
                              />
                              <label htmlFor="contract-active" className="form-check-label">
                                {contractStatus}
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mb-4">
                        <div className="d-flex justify-content-end">
                          <a className="btn btn-link" href={"/client/contract/detail/" + id_contract}>Cancel</a>
                          <button className="btn btn-primary ms-2" type="submit" disabled={isLoadingSubmit}>{isLoadingSubmit ? "Loading..." : "Update"}</button>
                        </div>
                      </div>
                    </form>
                  </section>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractEdit;


